import { TextField, TextFieldProps } from '@mui/material';
import { BUMPS_BY_UNITS } from 'Constants';
import { PatternString } from 'Pattern';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Units } from 'Types';

import { useSelectedBoardInstance } from 'utils/hooks';
import { bumpSelection, getPatternInputSelection } from 'utils/patternUtils';
import { bumpNumberDown, bumpNumberUp } from 'utils/utils';

type Props<T extends PatternString> = {
  InputProps?: TextFieldProps['InputProps'];
  id?: string;
  label?: string;
  onChange: (pattern: T) => void;
  onInputFragmentSelectionChange?: (
    startIndex: number,
    endIndex: number
  ) => void;
  value: T;
};

function PatternInput<T extends PatternString>(props: Props<T>) {
  const {
    id,
    InputProps,
    label,
    onChange,
    onInputFragmentSelectionChange,
    value,
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedBoard = useSelectedBoardInstance();
  const [forcedSelection, setForcedSelection] = useState<
    null | [number, number]
  >(null);

  const onKeyDown: TextFieldProps['onKeyDown'] = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const bumps = BUMPS_BY_UNITS[selectedBoard?.units ?? Units.IN];
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      onChange(bumpSelection(event, bumpNumberUp, bumps, setForcedSelection));
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      onChange(bumpSelection(event, bumpNumberDown, bumps, setForcedSelection));
    } else {
      setForcedSelection(null);
    }
  };

  const _onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value as T);
  };

  const clearForcedSelection = () => {
    setForcedSelection(null);
    onInputFragmentSelectionChange?.(0, 0);
  };

  const onDocumentSelectionChange = useCallback(() => {
    if (
      onInputFragmentSelectionChange == null ||
      !(document.activeElement instanceof HTMLInputElement) ||
      !ref.current?.contains(document.activeElement)
    ) {
      return;
    }

    const { startFragmentIndex, endFragmentIndex } = getPatternInputSelection(
      document.activeElement
    );

    onInputFragmentSelectionChange(startFragmentIndex, endFragmentIndex);
  }, [onInputFragmentSelectionChange]);

  useEffect(() => {
    const input = ref.current && ref.current.querySelector('input');
    if (input == null || forcedSelection == null) {
      return;
    }

    input.selectionStart = forcedSelection[0];
    input.selectionEnd = forcedSelection[1];
  });

  useEffect(() => {
    document.addEventListener('selectionchange', onDocumentSelectionChange);

    return () =>
      document.removeEventListener(
        'selectionchange',
        onDocumentSelectionChange
      );
  }, [onDocumentSelectionChange]);

  return (
    <TextField
      id={id}
      label={label == null ? 'Pattern' : label}
      variant="outlined"
      value={value}
      ref={ref}
      onBlur={clearForcedSelection}
      onChange={_onChange}
      onKeyDown={onKeyDown}
      onMouseDown={clearForcedSelection}
      style={{ width: '100%' }}
      InputProps={{ ...InputProps, ref: inputRef }}
    />
  );
}

export default PatternInput;
